import { create } from 'zustand';
import { authService } from '../services/auth-service';
import type { AuthUser, LoginRequest } from '../types/auth';

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  initialize: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  // Decoded from the stored token while the store is being created, so the very
  // first render already knows who is signed in. App calls initialize() from an
  // effect, which runs *after* the router has rendered — leaving every page's
  // first fetch to run with no user (e.g. companyId=0), firing a throwaway
  // request that then races the real one.
  user: authService.isAuthenticated() ? authService.getCurrentUser() : null,
  isLoading: false,
  error: null,

  // Kept as a safety net: re-reads the token on mount in case it changed
  // (another tab signing in or out) after the store was created.
  initialize: () => {
    set({ user: authService.isAuthenticated() ? authService.getCurrentUser() : null });
  },

  login: async (credentials: LoginRequest) => {
    set({ isLoading: true, error: null });
    try {
      await authService.login(credentials);
      const user = authService.getCurrentUser();
      set({ user, isLoading: false });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Username or password is incorrect';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  logout: () => {
    authService.logout();
    set({ user: null });
  },
}));
